import React, { useEffect, useState } from 'react';
import { EventTypeProcess } from '../../../types/ViewModel/EventTypeProcess';
import { ExpandedProcessWrapper, InstanceSectionWrapper, ToolboxProcessWrapper, Wrapper } from './ToolboxProcess.styles';
import { EventType } from '../../../types/ViewModel/EventType';
import { useEventTypeStore } from '../../../context/EventTypeContext';
import { EventTypeProcessInstance } from '../../../types/ViewModel/EventTypeProcessInstance';
import ToolboxButton from '../toolboxButton/ToolboxButton';
import eventTypeProcessUtils from '../../../utils/event/eventTypeProcessUtils';

type Props = {
    onChange: (prc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => void;
    evTypeProcess: EventTypeProcess | null;
    prcKey: string;
    level: number;
};

const ToolboxProcessComponent: React.FC<Props> = (props) => {
    const [processList, setProcessList] = useState<any[]>([]);
    const { setSelectedEventType } = useEventTypeStore();
    const { setDisplayNameAndSortProcessList } = eventTypeProcessUtils();

    useEffect(() => {
        if (props.evTypeProcess) {
            const prcList = getProcessList(props.evTypeProcess);
            setProcessList(prcList);
        }
    }, [props.evTypeProcess]);

    const createToolboxProcess = (prc: EventTypeProcess) => {
        return (
            <ToolboxProcessComponent
                onChange={handleExpandSubProcessClicked}
                evTypeProcess={prc}
                prcKey={props.prcKey}
                key={getNextPrcKey(prc)}
                level={props.level + 1}
            />
        );
    };

    const createToolboxButton = (
        prc: EventTypeProcess,
        prcInst: EventTypeProcessInstance | null,
        onChange: (prc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => void
    ) => {
        return (
            <ToolboxButton
                onChange={onChange}
                evTypeProcess={prc}
                prcInst={prcInst}
                key={getButtonPrcKey(prc)}
                prcKey={props.prcKey}
                level={props.level}
            />
        );
    };

    const handleProcessExpandClicked = (prc: EventTypeProcess) => {
        if (prc) {
            props.onChange(prc, null);
        }
    };

    const handleProcessInstanceExpandClicked = (prc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => {
        if (prc && prcInst) {
            setSelectedEventType(null);
            props.onChange(prc, prcInst);
        }
    };

    const handleExpandSubProcessClicked = (prc: EventTypeProcess, prcInst: EventTypeProcessInstance | null) => {
        props.onChange(prc, prcInst);
    };

    const getButtonPrcKey = (prc: EventTypeProcess) => {
        return getEventTypeKey(prc.eventType) + '-btn';
    };

    const getMandatoryList = (prcList: EventTypeProcess[]) => {
        return setDisplayNameAndSortProcessList(prcList.filter((prc) => prc.isMandatory));
    };

    const getOptionalList = (prcList: EventTypeProcess[]) => {
        return setDisplayNameAndSortProcessList(prcList.filter((prc) => !prc.isMandatory));
    };

    const createExpandedInstanceProcess = (prc: EventTypeProcess, prcInst: EventTypeProcessInstance) => {
        const processItemList: any[] = [];
        processItemList.push(createPrcInstanceButtons(prc, prcInst));
        if (prcInst.isExpanded) {
            processItemList.push(createExpandedProcess(prcInst.evTypePrcList, getInstanceKey(prc, prcInst)));
        }
        return processItemList;
    };

    const createPrcButton = (prc: EventTypeProcess) => {
        //prc.isMandatory = prc.eventType!.actualMandatory;
        return createToolboxButton(prc, null, handleProcessExpandClicked);
    };

    const createPrcInstanceButtons = (prc: EventTypeProcess, inst: EventTypeProcessInstance) => {
        return createToolboxButton(prc, inst, handleProcessInstanceExpandClicked);
    };

    const getEventTypeKey = (evType: EventType): string => {
        return props.prcKey + '-' + props.level.toString() + '-' + evType.eventTypeId.toString();
    };

    const getInstanceKey = (prc: EventTypeProcess, inst: EventTypeProcessInstance): string => {
        return props.prcKey + '-' + props.level.toString() + '-' + prc.eventType?.eventTypeId.toString() + '-' + inst.sequenceNr?.toString();
    };

    const getExpPrcKey = (keyPrefix: string): string => {
        return keyPrefix + '-exp';
    };

    const hasExpanded = (prcList: EventTypeProcess[]): boolean => {
        let exp = false;
        if (prcList && prcList.length > 0) {
            let keepGoing = true;
            let ind = 0;
            while (keepGoing) {
                if (prcList[ind].isExpanded) {
                    keepGoing = false;
                    exp = true;
                } else {
                    if (prcList[ind].evTypePrcInstanceList && prcList[ind].evTypePrcInstanceList.length > 0) {
                        const instExp = prcList[ind].evTypePrcInstanceList.find((inst) => inst.isExpanded);
                        if (instExp) {
                            keepGoing = false;
                            exp = true;
                        }
                    }
                }
                ind++;
                if (ind === prcList.length) {
                    keepGoing = false;
                }
            }
        }
        return exp;
    };

    const createExpandedProcess = (prcList: EventTypeProcess[], keyPrefix: string) => {
        if (prcList.length > 0) {
            const mandatoryList = getMandatoryList(prcList);
            const optionalList = getOptionalList(prcList);
            const isOptionalExpanded = !hasExpanded(mandatoryList);
            return (
                <ExpandedProcessWrapper key={getExpPrcKey(keyPrefix)} includeBorder={true}>
                    {mandatoryList && mandatoryList.length > 0 && (
                        <Wrapper>
                            {mandatoryList.map((prc) => {
                                return createToolboxProcess(prc);
                            })}
                        </Wrapper>
                    )}
                    {isOptionalExpanded && optionalList && optionalList.length > 0 && (
                        <Wrapper>
                            {optionalList.map((prc) => {
                                return createToolboxProcess(prc);
                            })}
                        </Wrapper>
                    )}
                </ExpandedProcessWrapper>
            );
        }
        return null;
    };

    const getNextPrcKey = (prc: EventTypeProcess) => {
        return getEventTypeKey(prc.eventType) + '-prc';
    };

    const getInstSectionKey = (prc: EventTypeProcess) => {
        return getEventTypeKey(prc.eventType) + '-inst';
    };

    const createProcessInstanceList = (prc: EventTypeProcess) => {
        const prcListSorted = prc.evTypePrcInstanceList.sort((a, b) => (a.sequenceNr > b.sequenceNr ? 1 : -1));
        return (
            <InstanceSectionWrapper key={getInstSectionKey(prc)}>
                {prcListSorted.map((inst) => {
                    return createExpandedInstanceProcess(prc, inst);
                })}
            </InstanceSectionWrapper>
        );
    };

    const getProcessList = (prc: EventTypeProcess) => {
        let processItemList: any[] = [];
        processItemList.push(createPrcButton(prc));
        if (prc.isExpanded && prc.evTypePrcInstanceList && prc.evTypePrcInstanceList.length > 0) {
            processItemList = processItemList.concat(createProcessInstanceList(prc));
        } else if (prc.isExpanded) {
            processItemList.push(createExpandedProcess(prc.evTypePrcList, getEventTypeKey(prc.eventType)));
        }
        return processItemList;
    };

    return (
        <>
            <ToolboxProcessWrapper>{processList}</ToolboxProcessWrapper>
        </>
    );
};

export default ToolboxProcessComponent;
